// Merkezi backend API client
// Tüm backend istekleri buradan geçer — JWT token otomatik eklenir
import { areAndroidHealthFeaturesDisabled, isNativePlatform } from "../utils/platformFeatures";
import { clearPersistentImageCache } from "./persistentImageCache";

export const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL || "https://api.aileyonetimi.com"
).replace(/\/+$/, "");

const TOKEN_KEY   = "fp:accessToken";
const REFRESH_KEY = "fp:refreshToken";
const USER_KEY    = "fp:user";
const CLIENT_INSTANCE_KEY = "fp:authClientInstanceId";
const COOKIE_SESSION_KEY = "fp:hasRefreshCookie";
const COOKIE_REFRESH_MARKER = "__http_only_cookie__";
// Mobil ağ gecikmesi, arka plan/ön plan geçişi ve uzun yaşayan SSE kurulumu
// sırasında tokenın istek yoldayken dolmasını önlemek için 3 dakika erken yenile.
// Refresh çağrısı single-flight olduğu için eşzamanlı istekler ek yük oluşturmaz.
const ACCESS_TOKEN_REFRESH_SKEW_MS = 3 * 60 * 1000;
const REFRESH_REQUEST_TIMEOUT_MS = 15 * 1000;
const PUBLIC_AUTH_PATHS = new Set([
  "/auth/login",
  "/auth/register",
  "/auth/social",
  "/auth/social/register",
  "/auth/forgot-password",
  "/auth/reset-password",
  "/auth/reset-password/validate",
  "/auth/verify-email",
]);
const PUBLIC_API_PATHS = new Set([
  "/finance/market-prices",
  "/website-traffic/install",
]);

let refreshState = null;
let sessionExpiredDispatched = false;
let lastProactiveRefreshToken = "";
let inMemoryAccessToken = "";
// Bir hesaba ait gecikmiş istek/401 cevabı, çıkış veya hesap değişiminden sonra
// yeni hesabın tokenıyla tekrar gönderilmemelidir. Bu sayaç yalnız oturum sahibi
// değiştiğinde artar; normal access-token yenilemeleri aynı nesilde kalır.
let authSessionGeneration = 0;

function createRefreshAttemptId() {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {}
  return `refresh-${Date.now()}-${Math.random().toString(36).slice(2, 14)}`;
}

function getOrCreateClientInstanceId() {
  try {
    const existing = localStorage.getItem(CLIENT_INSTANCE_KEY) || "";
    if (existing) return existing;
    const created = createRefreshAttemptId();
    localStorage.setItem(CLIENT_INSTANCE_KEY, created);
    return created;
  } catch {
    return createRefreshAttemptId();
  }
}

function userIdentity(user) {
  return String(user?.uid || user?.firebase_uid || user?.id || "").trim();
}

export function clearUserScopedStorage() {
  clearPersistentImageCache().catch(() => {});
  try {
    [
      "fp:familyCache",
      "fp:aktivFamilyId",
      "fp:notifCache",
      "fp:seenNotifIds",
      "fp:dismissedNotifications",
      "fp:calendar-integration:v1",
    ].forEach((key) => localStorage.removeItem(key));

    // Sağlık, finans, çocuk, belge, sohbet ve aktivite snapshot'ları hassas veri
    // içerir. Ortak cihazda çıkış yapan kullanıcının verisi tarayıcıda kalmamalıdır.
    const prefixes = [
      "fp:dc:",
      "familyPlanner.chatCache.v2.",
      "familyPlanner.activityFeed.v1.",
      "familyPlanner.financeToolsCache.",
      "familyPlanner.healthRecords.",
      "familyPlanner.chatDraft.",
      "chatAutoSaveMedia.",
    ];
    const exactSensitiveKeys = [
      "cycle_page_cache_v2",
      "cycle_analysis_cache_v1",
      "familyPlanner.financeSavings",
      "familyPlanner.homeInstallments",
    ];
    exactSensitiveKeys.forEach((key) => {
      localStorage.removeItem(key);
      try { sessionStorage.removeItem(key); } catch {}
    });
    const remove = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && prefixes.some((prefix) => key.startsWith(prefix))) remove.push(key);
    }
    remove.forEach((key) => localStorage.removeItem(key));
    try {
      const sessionRemove = [];
      for (let i = 0; i < sessionStorage.length; i += 1) {
        const key = sessionStorage.key(i);
        if (key && prefixes.some((prefix) => key.startsWith(prefix))) sessionRemove.push(key);
      }
      sessionRemove.forEach((key) => sessionStorage.removeItem(key));
    } catch {}
    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event("fp:userScopedStorageCleared"));
    }
  } catch {}
}

// ── Token yönetimi ──────────────────────────────────────────────
export function getAccessToken() {
  try {
    if (isNativePlatform()) return localStorage.getItem(TOKEN_KEY) || "";
    if (inMemoryAccessToken) return inMemoryAccessToken;

    const sessionToken = sessionStorage.getItem(TOKEN_KEY) || "";
    if (sessionToken) {
      inMemoryAccessToken = sessionToken;
      return sessionToken;
    }

    // Kesintisiz guvenlik gecisi: onceki web surumunun localStorage'a yazdigi
    // tokeni ayni sekme oturumuna bir kez tasi ve kalici kopyayi hemen sil.
    const legacyToken = localStorage.getItem(TOKEN_KEY) || "";
    if (legacyToken) {
      sessionStorage.setItem(TOKEN_KEY, legacyToken);
      localStorage.removeItem(TOKEN_KEY);
      inMemoryAccessToken = legacyToken;
    }
    return legacyToken;
  } catch {
    return inMemoryAccessToken;
  }
}
export function getRefreshToken() {
  try {
    if (isNativePlatform()) return localStorage.getItem(REFRESH_KEY) || "";
    const sessionToken = sessionStorage.getItem(REFRESH_KEY) || "";
    if (sessionToken) return sessionToken;
    const legacyToken = localStorage.getItem(REFRESH_KEY) || "";
    if (legacyToken) {
      sessionStorage.setItem(REFRESH_KEY, legacyToken);
      localStorage.removeItem(REFRESH_KEY);
      return legacyToken;
    }
    if (localStorage.getItem(COOKIE_SESSION_KEY) === "1") {
      return COOKIE_REFRESH_MARKER;
    }
    return "";
  } catch { return ""; }
}
export function getStoredUser()   {
  try { const r = localStorage.getItem(USER_KEY); return r ? JSON.parse(r) : null; }
  catch { return null; }
}

export function saveSession({ accessToken, refreshToken, refreshCookie = false, user } = {}) {
  try {
    if (user) {
      const previousUid = userIdentity(getStoredUser());
      const nextUid = userIdentity(user);
      if (nextUid && previousUid !== nextUid) authSessionGeneration += 1;
      if (previousUid && nextUid && previousUid !== nextUid) clearUserScopedStorage();
    }
    if (accessToken) {
      if (isNativePlatform()) {
        localStorage.setItem(TOKEN_KEY, accessToken);
      } else {
        inMemoryAccessToken = accessToken;
        sessionStorage.setItem(TOKEN_KEY, accessToken);
        localStorage.removeItem(TOKEN_KEY);
      }
    }
    if (refreshToken) {
      if (isNativePlatform()) {
        localStorage.setItem(REFRESH_KEY, refreshToken);
      } else {
        sessionStorage.setItem(REFRESH_KEY, refreshToken);
        localStorage.removeItem(REFRESH_KEY);
        localStorage.removeItem(COOKIE_SESSION_KEY);
      }
    }
    if (refreshCookie && !isNativePlatform()) {
      localStorage.removeItem(REFRESH_KEY);
      sessionStorage.removeItem(REFRESH_KEY);
      localStorage.setItem(COOKIE_SESSION_KEY, "1");
    }
    if (user)         localStorage.setItem(USER_KEY,    JSON.stringify(user));
    if (accessToken || refreshToken) sessionExpiredDispatched = false;
    if (accessToken) {
      lastProactiveRefreshToken = accessTokenShouldRefresh(accessToken)
        ? accessToken
        : "";
    }
    if (accessToken && typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("fp:accessTokenChanged", {
        detail: { accessToken, refreshToken: refreshToken || getRefreshToken() },
      }));
    }
  } catch {}
}

export function clearSession({ clearUserData = true } = {}) {
  try {
    authSessionGeneration += 1;
    // Manuel çıkış/hesap silme sonrasında geç kalan istekler ikinci bir
    // sessionExpired olayı üretmemeli. Yeni giriş saveSession ile sıfırlar.
    sessionExpiredDispatched = true;
    localStorage.removeItem(TOKEN_KEY);
    try { sessionStorage.removeItem(TOKEN_KEY); } catch {}
    inMemoryAccessToken = "";
    localStorage.removeItem(REFRESH_KEY);
    try { sessionStorage.removeItem(REFRESH_KEY); } catch {}
    localStorage.removeItem(COOKIE_SESSION_KEY);
    localStorage.removeItem(USER_KEY);
    lastProactiveRefreshToken = "";
    if (clearUserData) clearUserScopedStorage();
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("fp:accessTokenChanged", {
        detail: { accessToken: "" },
      }));
    }
  } catch {}
}

function dispatchSessionExpiredOnce() {
  if (sessionExpiredDispatched) return;
  sessionExpiredDispatched = true;
  clearSession();
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("fp:sessionExpired"));
  }
}

function sessionExpiredError() {
  const err = new Error("Oturum süresi doldu.");
  err.code = "session_expired";
  err.status = 401;
  return err;
}

function serviceUnavailableError() {
  const err = new Error("Sunucu geçici olarak kullanılamıyor.");
  err.code = "service_unavailable";
  err.status = 503;
  return err;
}

function staleSessionRequestError() {
  const err = new Error("İstek, oturum değiştiği için iptal edildi.");
  err.code = "request_cancelled";
  err.status = 0;
  return err;
}

function assertSessionGeneration(expectedGeneration) {
  if (Number(expectedGeneration) !== authSessionGeneration) {
    throw staleSessionRequestError();
  }
}

function authPathRequiresSession(path) {
  const pathname = String(path || "").split("?")[0];
  return pathname !== "/auth/refresh"
    && !PUBLIC_AUTH_PATHS.has(pathname)
    && !PUBLIC_API_PATHS.has(pathname);
}

function accessTokenExpiryMs(token) {
  try {
    const payloadPart = String(token || "").split(".")[1];
    if (!payloadPart) return null;
    const normalized = payloadPart.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), "=");
    const payload = JSON.parse(atob(padded));
    const expiresAtMs = Number(payload?.exp) * 1000;
    if (!Number.isFinite(expiresAtMs) || expiresAtMs <= 0) return null;
    return expiresAtMs;
  } catch {
    // Biçimi bilinmeyen token sunucuda doğrulanır; burada oturumu yanlışlıkla
    // sonlandırmak yerine mevcut 401 + refresh güvenlik ağı korunur.
    return null;
  }
}

function accessTokenShouldRefresh(token) {
  const expiresAtMs = accessTokenExpiryMs(token);
  return expiresAtMs !== null && expiresAtMs <= Date.now() + ACCESS_TOKEN_REFRESH_SKEW_MS;
}


// ── Ağ hatası tespiti ────────────────────────────────────────────
// fetch() ağa ulaşamazsa (internet yok, DNS hatası vb.) TypeError fırlatır.
// Bu hatayı sunucu kaynaklı auth hatalarından ayırt etmek için flag ekliyoruz.
export function isNetworkError(err) {
  if (!err) return false;
  if (err.isNetworkError) return true;
  // fetch'in fırlattığı TypeError'lar
  if (err instanceof TypeError) return true;
  // Bazı ortamlarda mesaj bazlı tespit
  const msg = (err.message || "").toLowerCase();
  return msg.includes("failed to fetch") ||
         msg.includes("network request failed") ||
         msg.includes("networkerror") ||
         msg.includes("the internet connection appears to be offline");
}

// ── API performans ölçümü ───────────────────────────────────────
// Yalnızca console çıktısı üretir; istek akışını, payload'ı veya sonucu değiştirmez.
// Her çağrıya sıra numarası vererek duplicate istekleri ve 401 sonrası tekrarları
// cihaz loglarında açıkça ayırt etmeyi sağlar.
let apiPerfSequence = 0;

function perfNow() {
  try {
    if (typeof performance !== "undefined" && typeof performance.now === "function") {
      return performance.now();
    }
  } catch {}
  return Date.now();
}

function apiPerfLog(entry) {
  try {
    if (process.env.NODE_ENV === "production") return;
    const duration = Math.max(0, Math.round(entry.endedAt - entry.startedAt));
    const method = String(entry.method || "GET").toUpperCase();
    const status = entry.status ?? "NETWORK_ERROR";
    const suffix = entry.note ? ` | ${entry.note}` : "";
    const level = entry.failed || duration >= 3000 ? "warn" : "info";
    console[level](
      `[API PERF #${entry.id}] ${method} ${entry.path} | ${status} | ${duration} ms${suffix}`
    );
  } catch {}
}

// ── HTTP istek ──────────────────────────────────────────────────
export async function apiRequest(path, options = {}, perfContext = {}) {
  // Android Lite başlığı yalnızca sağlık/çocuk modüllerini gizlemek için kullanılır.
  // Görevler gibi Android’de desteklenen endpointler bu başlığı açıkça kapatabilir.
  const {
    skipAndroidLiteHeader = false,
    skipAuthRefresh = false,
    ...fetchOptions
  } = options || {};
  const perfId = ++apiPerfSequence;
  const startedAt = perfNow();
  const method = String(fetchOptions.method || "GET").toUpperCase();
  const networkAttempt = Number(perfContext.networkAttempt || perfContext.attempt || 1);
  const authAttempt = Number(perfContext.authAttempt || 0);
  const authManaged = authPathRequiresSession(path) && !skipAuthRefresh;
  const requestSessionGeneration = Number(
    perfContext.sessionGeneration ?? authSessionGeneration
  );
  let res;

  if (authManaged) {
    assertSessionGeneration(requestSessionGeneration);
    await ensureAccessToken({ source: "api_request" });
    assertSessionGeneration(requestSessionGeneration);
  }
  const token = getAccessToken();

  try {
    res = await fetch(`${API_BASE_URL}${path}`, {
      ...fetchOptions,
      cache: "no-store",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "Cache-Control": "no-cache",
        ...(areAndroidHealthFeaturesDisabled() && !skipAndroidLiteHeader ? { "X-App-Variant": "android-lite" } : {}),
        "X-Refresh-Transport": isNativePlatform() ? "body" : "cookie",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(fetchOptions.headers || {}),
      },
    });
  } catch (networkErr) {
    apiPerfLog({
      id: perfId,
      path,
      method,
      status: "NETWORK_ERROR",
      startedAt,
      endedAt: perfNow(),
      failed: true,
      note: `attempt=${networkAttempt}; authAttempt=${authAttempt}; ${networkErr?.message || "fetch failed"}`,
    });
    networkErr.isNetworkError = true;
    throw networkErr;
  }

  const responseReceivedAt = perfNow();
  const data = await res.json().catch(() => ({}));
  const parsedAt = perfNow();

  if (authManaged) assertSessionGeneration(requestSessionGeneration);

  if (
    res.status === 401 &&
    authManaged &&
    authAttempt === 0
  ) {
    apiPerfLog({
      id: perfId,
      path,
      method,
      status: res.status,
      startedAt,
      endedAt: parsedAt,
      failed: true,
      note: `attempt=${networkAttempt}; authAttempt=${authAttempt}; token yenileme kontrolü; bodyParse=${Math.round(parsedAt - responseReceivedAt)}ms`,
    });

    // İstek eski token ile yoldayken başka bir istek refresh'i tamamlamış olabilir.
    // Bu durumda ikinci bir refresh açmadan güncel token ile yalnızca bir kez dene.
    const latestToken = getAccessToken();
    if (latestToken && latestToken !== token) {
      return apiRequest(path, options, {
        ...perfContext,
        networkAttempt,
        authAttempt: authAttempt + 1,
        sessionGeneration: requestSessionGeneration,
      });
    }

    await ensureAccessToken({ forceRefresh: true, source: "api_401" });
    assertSessionGeneration(requestSessionGeneration);
    return apiRequest(path, options, {
      ...perfContext,
      networkAttempt,
      authAttempt: authAttempt + 1,
      sessionGeneration: requestSessionGeneration,
    });
  }

  apiPerfLog({
    id: perfId,
    path,
    method,
    status: res.status,
    startedAt,
    endedAt: parsedAt,
    failed: !res.ok || data?.ok === false,
    note: `attempt=${networkAttempt}; authAttempt=${authAttempt}; bodyParse=${Math.round(parsedAt - responseReceivedAt)}ms`,
  });

  if (!res.ok || data?.ok === false) {
    const err = new Error(data?.message || data?.error || `İstek başarısız (${res.status})`);
    err.code = data?.code || null;
    err.status = res.status;
    err.payload = data;
    throw err;
  }
  return data;
}


// ── Geçici ağ hataları için kontrollü GET retry ─────────────────────────────
// Yalnızca çağıran modül açıkça kullandığında devreye girer. POST/PATCH/DELETE
// işlemlerini otomatik tekrar etmez; böylece çift kayıt riski oluşturmaz.
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export async function apiRequestWithRetry(path, options = {}, retryOptions = {}) {
  const method = String(options.method || "GET").toUpperCase();
  if (method !== "GET") return apiRequest(path, options);

  const delays = Array.isArray(retryOptions.delays)
    ? retryOptions.delays
    : [500, 1500];
  const jitter = Number.isFinite(Number(retryOptions.jitter))
    ? Math.max(0, Number(retryOptions.jitter))
    : 120;
  const shouldContinue = typeof retryOptions.shouldContinue === "function"
    ? retryOptions.shouldContinue
    : () => true;

  let lastError;
  for (let attemptIndex = 0; attemptIndex <= delays.length; attemptIndex += 1) {
    if (!shouldContinue()) {
      const cancelledError = new Error("İstek iptal edildi.");
      cancelledError.code = "request_cancelled";
      throw cancelledError;
    }

    try {
      return await apiRequest(path, options, { networkAttempt: attemptIndex + 1 });
    } catch (error) {
      lastError = error;
      const status = Number(error?.status || 0);
      const retryable =
        isNetworkError(error) ||
        status === 429 ||
        status === 502 ||
        status === 503 ||
        status === 504;

      if (!retryable || attemptIndex >= delays.length) throw error;

      const baseDelay = Math.max(0, Number(delays[attemptIndex]) || 0);
      const jitterOffset = jitter > 0
        ? Math.round((Math.random() * 2 - 1) * jitter)
        : 0;
      await wait(Math.max(0, baseDelay + jitterOffset));
    }
  }

  throw lastError;
}

// ── Dosya yükleme (multipart/form-data, ilerleme takibi destekli) ──
// apiRequest'ten farkı:
//  - Content-Type ayarlamıyor (tarayıcı FormData için boundary'yi
//    otomatik ekler)
//  - XMLHttpRequest kullanıyor (fetch upload ilerlemesini desteklemiyor),
//    onProgress({0..100}) ile yükleme yüzdesi raporlanır
export async function apiUpload(path, formData, { onProgress } = {}, authContext = {}) {
  const authAttempt = Number(authContext.authAttempt || 0);
  const requestSessionGeneration = Number(
    authContext.sessionGeneration ?? authSessionGeneration
  );
  assertSessionGeneration(requestSessionGeneration);
  await ensureAccessToken({ source: "api_upload" });
  assertSessionGeneration(requestSessionGeneration);
  const token = getAccessToken();

  const { status, data } = await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${API_BASE_URL}${path}`);
    xhr.withCredentials = true;
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);

    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
      };
    }

    xhr.onload = () => {
      let body = {};
      try { body = JSON.parse(xhr.responseText || "{}"); } catch {}
      resolve({ status: xhr.status, data: body });
    };
    xhr.onerror = () => reject(new Error("Dosya yüklenemedi. İnternet bağlantısını ve dosya boyutunu kontrol edip tekrar deneyin."));
    xhr.onabort = () => reject(new Error("Dosya yükleme iptal edildi."));
    xhr.send(formData);
  });

  assertSessionGeneration(requestSessionGeneration);

  if (status === 401 && authAttempt === 0) {
    const latestToken = getAccessToken();
    if (latestToken && latestToken !== token) {
      return apiUpload(path, formData, { onProgress }, {
        authAttempt: 1,
        sessionGeneration: requestSessionGeneration,
      });
    }
    await ensureAccessToken({ forceRefresh: true, source: "upload_401" });
    assertSessionGeneration(requestSessionGeneration);
    return apiUpload(path, formData, { onProgress }, {
      authAttempt: 1,
      sessionGeneration: requestSessionGeneration,
    });
  }

  if (status === 401) {
    dispatchSessionExpiredOnce();
    throw sessionExpiredError();
  }

  if (status < 200 || status >= 300 || data?.ok === false) {
    const err = new Error(data?.message || data?.error || `İstek başarısız (${status})`);
    err.code = data?.code || null;
    err.status = status;
    err.payload = data;
    throw err;
  }
  return data;
}

async function performRefresh(refreshToken) {
  const attemptId = createRefreshAttemptId();
  const clientInstanceId = getOrCreateClientInstanceId();
  const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
  const timeout = controller
    ? setTimeout(() => controller.abort(), REFRESH_REQUEST_TIMEOUT_MS)
    : null;
  try {
    const res = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "X-Refresh-Transport": isNativePlatform() ? "body" : "cookie",
        "X-Refresh-Attempt-Id": attemptId,
        "X-Refresh-Protocol": "2",
        "X-Refresh-Client": isNativePlatform() ? "native-webview" : "web",
        "X-Client-Instance-Id": clientInstanceId,
      },
      body: JSON.stringify({
        ...(refreshToken !== COOKIE_REFRESH_MARKER ? { refreshToken } : {}),
        rotateRefreshToken: true,
      }),
      ...(controller ? { signal: controller.signal } : {}),
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok && data.accessToken) {
      // Çıkış veya başka hesapla giriş refresh sürerken gerçekleştiyse eski
      // yanıt yeni oturumu kesinlikle geri yazmamalıdır.
      if (getRefreshToken() !== refreshToken) return "superseded";
      saveSession({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken || refreshToken,
        refreshCookie: data.refreshCookie === true,
        user: data.user,
      });
      return "refreshed";
    }

    if (getRefreshToken() !== refreshToken) return "superseded";

    // Yalnizca backend'in acikca kesinlestirdigi guvenlik/oturum durumlari
    // logout uretebilir. Eski backend'in genel REFRESH_TOKEN_INVALID cevabi,
    // rotation cevabi kaybi da olabilecegi icin yerel oturumu silmemelidir.
    const terminalCodes = new Set([
      "SESSION_REVOKED",
      "SESSION_EXPIRED",
      "USER_DISABLED",
      "REFRESH_TOKEN_REPLAYED",
      "REFRESH_TOKEN_UNKNOWN",
    ]);
    if ([400, 401, 403].includes(res.status) && terminalCodes.has(data?.code)) {
      return "terminal";
    }
    return "unavailable";
  } catch {
    return "unavailable";
  } finally {
    if (timeout) clearTimeout(timeout);
  }
}

function refreshSessionOnce(refreshToken, source) {
  if (refreshState?.refreshToken === refreshToken) return refreshState.promise;

  const refreshStartedAt = perfNow();
  const runRefresh = async () => {
    if (getRefreshToken() !== refreshToken) return "superseded";
    return performRefresh(refreshToken);
  };
  const coordinatedRefresh = typeof navigator !== "undefined" && navigator.locks?.request
    ? navigator.locks.request("fp:auth-refresh", { mode: "exclusive" }, runRefresh)
    : runRefresh();
  const pending = coordinatedRefresh.then((result) => {
    const duration = Math.max(0, Math.round(perfNow() - refreshStartedAt));
    try {
      const success = result === "refreshed" || result === "superseded";
      const label = success
        ? "SUCCESS"
        : result === "terminal"
          ? "FAILED"
          : "TEMPORARILY_UNAVAILABLE";
      console[success ? "info" : "warn"](
        `[API PERF] TOKEN_REFRESH | ${label} | ${duration} ms | source=${source || "unknown"}`
      );
    } catch {}
    return result;
  });

  const state = { refreshToken, promise: null };
  state.promise = pending.finally(() => {
    if (refreshState === state) refreshState = null;
  });
  refreshState = state;
  return state.promise;
}

/**
 * Korunan REST/SSE/native istekleri için geçerli access token sağlar.
 * Aynı JS çalışma alanındaki bütün çağrılar tek refresh Promise'ini paylaşır.
 */
export async function ensureAccessToken({ forceRefresh = false, source = "unknown" } = {}) {
  // Bir 401 başka bir istekte refresh başlattıysa access token JWT süresine göre
  // hâlâ geçerli görünebilir. Bu sırada eski tokenla yeni istek göndermek 401
  // kümeleri üretir. Devam eden tekil refresh'i önce bekleyerek bütün korunan
  // REST/upload/dosya yenileme çağrılarını aynı token otoritesinde buluştur.
  const pendingRefresh = refreshState;
  if (pendingRefresh?.promise) {
    const pendingResult = await pendingRefresh.promise;
    if (pendingResult === "refreshed" || pendingResult === "superseded") {
      const latestToken = getAccessToken();
      if (latestToken) return latestToken;
      dispatchSessionExpiredOnce();
      throw sessionExpiredError();
    }
    if (pendingResult === "terminal") {
      dispatchSessionExpiredOnce();
      throw sessionExpiredError();
    }

    const usableToken = getAccessToken();
    const usableExpiryMs = accessTokenExpiryMs(usableToken);
    if (!forceRefresh && usableToken && usableExpiryMs !== null && usableExpiryMs > Date.now()) {
      return usableToken;
    }
    throw serviceUnavailableError();
  }

  const currentToken = getAccessToken();
  const refreshToken = getRefreshToken();
  const expiresAtMs = accessTokenExpiryMs(currentToken);
  const shouldRefresh = accessTokenShouldRefresh(currentToken);

  if (!forceRefresh) {
    if (currentToken && !shouldRefresh) return currentToken;
    if (!currentToken && !refreshToken) return "";

    // Cihaz saati ileri ayarlıysa yeni üretilen token da yerelde süresi dolmuş
    // görünebilir. Aynı token için sürekli proaktif refresh yerine sunucu
    // doğrulamasına geç; gerçek 401 olursa aşağıdaki zorunlu yol çalışır.
    if (
      currentToken &&
      shouldRefresh &&
      lastProactiveRefreshToken === currentToken &&
      refreshState?.refreshToken !== refreshToken
    ) {
      return currentToken;
    }
  }

  if (!refreshToken) {
    dispatchSessionExpiredOnce();
    throw sessionExpiredError();
  }

  if (!forceRefresh && currentToken && shouldRefresh) {
    lastProactiveRefreshToken = currentToken;
  }

  const result = await refreshSessionOnce(refreshToken, source);
  if (result === "refreshed" || result === "superseded") {
    const latestToken = getAccessToken();
    if (latestToken) return latestToken;
    dispatchSessionExpiredOnce();
    throw sessionExpiredError();
  }
  if (result === "unavailable") {
    // Proaktif pencere içinde token henüz gerçekten dolmadıysa kullanıcı
    // isteğini kesme; mevcut token sunucuda geçerli olmaya devam eder.
    if (!forceRefresh && currentToken && expiresAtMs !== null && expiresAtMs > Date.now()) {
      return currentToken;
    }
    throw serviceUnavailableError();
  }

  dispatchSessionExpiredOnce();
  throw sessionExpiredError();
}
